'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';

//* Components
import Icon from '../Icon';
import Image from '@/components/common/Image';
import CustomLink from '@/components/common/CustomLink';
import { Text, useTranslation } from '@weareconceptstudio/core';

//* Style
import ButtonStyle from './style';

const Button = ({
	btnType,
	text,
	doneText = 'added',
	isDisabled,
	disabled,
	className,
	onClick,
	children,
	url,
	customLinkProps,
	target,
	type = 'button',
	size,
	hasAdd,
	inCart = false,
	tooltipText = 'outOfStockMessageDefault',
	useInfoShow = true,
}) => {
	const isBtnDisabled = Boolean(isDisabled ?? disabled) || inCart;
	const Component = useMemo(() => (url ? CustomLink : 'button'), [url]);
	const customProps = useMemo(() => {
		if (url) {
			return { ...customLinkProps, url, target };
		}
		return { type, disabled: isBtnDisabled };
	}, [url, customLinkProps, target, type, isBtnDisabled]);
	const { translate } = useTranslation();

	let button;

	//! State
	const [isAddedBag, setIsAddedBug] = useState(false);
	const [isTooltipOpen, setIsTooltipOpen] = useState(false);

	//! Refs
	const avocado1Ref = useRef();
	const avocado2Ref = useRef();
	const avocado3Ref = useRef();
	const textRef = useRef();
	const tooltipWrapRef = useRef();

	const handleOutsideClick = useCallback((e) => {
		if (tooltipWrapRef.current && !tooltipWrapRef.current.contains(e.target)) {
			setIsTooltipOpen(false);
		}
	}, []);

	useEffect(() => {
		if (!isTooltipOpen) return;
		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [isTooltipOpen, handleOutsideClick]);

	const displayAdded = inCart || isAddedBag;

	const handleAddToBag = useCallback(
		(e) => {
			e.preventDefault();
			e.stopPropagation();

			const timeline = gsap.timeline({ repeat: -1, delay: 0.1 });

			gsap.to(textRef.current, { opacity: 0 });

			timeline.to([avocado1Ref.current, avocado2Ref.current, avocado3Ref.current], {
				top: '50%',
				stagger: 0.1,
			});

			timeline.to([avocado1Ref.current, avocado2Ref.current, avocado3Ref.current], {
				top: '150%',
				stagger: 0.1,
			});

			timeline.to([avocado1Ref.current, avocado2Ref.current, avocado3Ref.current], {
				top: '-100%',
				duration: 0.001,
			});

			Promise.resolve(onClick?.(e)).finally(() => {
				setTimeout(() => {
					setIsAddedBug(true);

					timeline.kill();

					gsap.set([avocado1Ref.current, avocado2Ref.current, avocado3Ref.current], {
						top: '-100%',
					});

					gsap.to(textRef.current, {
						opacity: 1,
						delay: 0.2,
					});
				}, 1000);
			});
		},
		[onClick]
	);

	switch (btnType) {
		case 'float-underline':
			button = (
				<ButtonStyle className={`${btnType} ${isBtnDisabled ? 'disabled' : ''} ${className || ''}`}>
					<Component
						{...customProps}
						onClick={onClick}>
						{children || (
							<Text
								className={'h2 font-futura-regular button-text'}
								text={text}
							/>
						)}
					</Component>
				</ButtonStyle>
			);
			break;

		case 'arrow':
			button = (
				<ButtonStyle className={`${btnType} ${isBtnDisabled ? 'disabled' : ''} ${className || ''}`}>
					<Component
						{...customProps}
						onClick={onClick}>
						{children || <Icon name={'arrow-big'} />}
					</Component>
				</ButtonStyle>
			);
			break;

		case 'out-of-stock':
			button = (
				<ButtonStyle
					ref={tooltipWrapRef}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setIsTooltipOpen(true);
					}}
					className={`default tooltip-wrap ${size || ''} ${className || ''} ${isTooltipOpen ? 'tooltip-open' : ''} ${btnType} ${useInfoShow ? '' : 'un-active'} ${isBtnDisabled ? 'disabled' : ''} ${!tooltipText ? 'tooltip-text-inactive' : ''}`}>
					<Component {...customProps}>
						{useInfoShow ? (
							<Icon
								name={'info'}
								className={'info-icon'}
							/>
						) : null}

						{translate(text)}
					</Component>

					{useInfoShow ? (
						<>
							{isTooltipOpen ? (
								<>
									{tooltipText ? (
										<div className='tooltip-text'>
											<Text
												className={'h6 font-futura-regular purple'}
												text={tooltipText}
											/>
										</div>
									) : null}
								</>
							) : null}
						</>
					) : null}
				</ButtonStyle>
			);
			break;

		case 'link-hover':
			button = (
				<ButtonStyle className={`${btnType} ${isBtnDisabled ? 'disabled' : ''} ${className || ''}`}>
					<Component
						{...customProps}
						onClick={onClick}>
						<Text
							className={'font-futura-medium h4 purple button-text'}
							text={translate(text)}
						/>
					</Component>
				</ButtonStyle>
			);
			break;

		case 'secondary':
			button = (
				<ButtonStyle className={`${btnType} ${isBtnDisabled ? 'disabled' : ''} ${className || ''}`}>
					<Component
						{...customProps}
						onClick={onClick}>
						<Text
							className={'font-futura-medium h4 purple button-text'}
							text={translate(text)}
						/>
					</Component>
				</ButtonStyle>
			);
			break;

		default:
			button = (
				<ButtonStyle className={`default ${size || ''} ${className || ''} ${isBtnDisabled ? 'disabled' : ''}`}>
					<Component
						{...customProps}
						onClick={hasAdd && !inCart ? handleAddToBag : onClick}>
						{children || (
							<Text
								ref={textRef}
								text={displayAdded ? doneText : text}
								className={'font-futura-medium h5 purple button-text'}
							/>
						)}

						{hasAdd && (
							<>
								<Image
									ref={avocado1Ref}
									className={'avocado-1'}
									src={'/images/avocado.svg'}
								/>
								<Image
									ref={avocado2Ref}
									className={'avocado-2'}
									src={'/images/avocado.svg'}
								/>
								<Image
									ref={avocado3Ref}
									className={'avocado-3'}
									src={'/images/avocado.svg'}
								/>
							</>
						)}
					</Component>
				</ButtonStyle>
			);
			break;
	}
	return button;
};

export default Button;
